'use client'

import { Typography } from '@repo/web-ui/components/typography'
import Image from 'next/image'
import { Link } from '@repo/web-ui/components/link'
import { useTranslations } from 'next-intl'
import { AppRoutes } from '@repo/web-utils/app-routes'

import { Button } from '@web/components/prod/button/button'
import { useMyAccountCardVariant } from '../_hooks/use-my-account-card-variant'
import { Card, CardSkeleton } from './card'
import { MyAccountEmptyBlock } from './empty-block'
import { useUserOwnFlowsQuery } from '@web/gen/user'
import { FlowPreviewSort } from '@web/gen/types'

export function MyAccountRecentGenerations() {
  const { title, description } = useMyAccountCardVariant('flow-prompts')
  const t = useTranslations('pages.my_account.widgets')
  const { isPending, data } = useUserOwnFlowsQuery({
    take: 10, // Take 10 since some could be empty
    sort: FlowPreviewSort.Newest,
  })

  const recentGenerations = (data?.flowsPreviewUserFlows.previews ?? []).filter(
    preview => preview.thumbnailUrl, // Only show flows with a thumbnail
  )

  if (isPending) {
    return <CardSkeleton />
  }

  if (!recentGenerations.length) {
    return <MyAccountEmptyBlock variant="flow-prompts" />
  }

  return (
    <Card className="flex grow flex-col gap-4">
      <div className="@lg:flex-row flex flex-col gap-2">
        <div className="flex grow flex-col">
          <Typography variant="heading-3" className="capitalize text-gray-100">
            {title}
          </Typography>
          {description ? <Typography variant="body">{description}</Typography> : null}
        </div>
        <div className="@lg:self-auto self-start">
          <Button asChild variant="normal" className="h-9 shrink-0 px-3">
            <Link href={AppRoutes.STUDIO_TOOL_FLOW}>{t('generate_images')}</Link>
          </Button>
        </div>
      </div>
      <div className="@lg:grid-cols-4 grid grid-cols-2 gap-3">
        {/* Force 4 placeholder divs always, even if no Thumbnail's available */}
        {[0, 1, 2, 3].map((_, index) => {
          const { flowId, thumbnailUrl } = recentGenerations[index] || {}
          return (
            <div key={flowId ?? index} className="flex flex-col gap-2">
              {thumbnailUrl ? (
                <Link
                  href={`${AppRoutes.STUDIO_TOOL_FLOW}${flowId}`}
                  className="block hover:opacity-90"
                >
                  <Image
                    width={140}
                    height={100}
                    src={thumbnailUrl}
                    alt={flowId}
                    className="aspect-[3/2] w-full rounded object-cover shadow-md"
                  />
                </Link>
              ) : null}
            </div>
          )
        })}
      </div>
    </Card>
  )
}
